import {
  Autocomplete,
  Stack,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { CargaFormatoRow } from "../../../api/reports";
import { downloadReportFile } from "../../../components/utils/downloadXlsx";
import { useCentros, type Centro } from "../../../hooks/useCentros";
import { useProfesores, type Profesor } from "../../../hooks/useProfesores";
import { useCargaFormatoReport } from "../../../hooks/useReportJob";

export default function CargaFormatoPage() {
  const { t } = useTranslation<"translation">();
  const [desde, setDesde] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [hasta, setHasta] = useState(dayjs().format("YYYY-MM-DD"));
  const [profSel, setProfSel] = useState<Profesor | null>(null);
  const [centroSel, setCentroSel] = useState<Centro | null>(null);

  const { rows: profesores } = useProfesores();
  const { rows: centros } = useCentros();

  const { run, useStatus } = useCargaFormatoReport();
  const [jobId, setJobId] = useState<string>();
  const { data: status } = useStatus(jobId);

  const lanzar = async () => {
    const payload = {
      desde,
      hasta,
      profesorId: profSel?.id,
      centroId: centroSel?.id,
    };
    const { jobId } = await run(payload);
    setJobId(jobId);
  };

  const handleDownload = async () => {
    if (status?.result?.url) {
      try {
        await downloadReportFile(status.result.url);
      } catch (error) {
        console.error("Error al descargar:", error);
      }
    }
  };

  const rows = status?.result?.rows ?? [];

  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t("cargaFormato.titulo")}</Typography>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <TextField
          type="date"
          size="small"
          label={t("cargaFormato.filtros.desde")}
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
        />
        <TextField
          type="date"
          size="small"
          label={t("cargaFormato.filtros.hasta")}
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
        />
        <Autocomplete
          options={profesores}
          getOptionLabel={(o) => o.nombre}
          value={profSel}
          onChange={(_, v) => setProfSel(v)}
          renderInput={(p) => (
            <TextField {...p} label={t("filters.profesor")} size="small" />
          )}
          sx={{ width: 220 }}
        />
        <Autocomplete
          options={centros.filter((c) => c.nombre !== "OTHER")}
          getOptionLabel={(o) => o.nombre}
          value={centroSel}
          onChange={(_, v) => setCentroSel(v)}
          renderInput={(p) => (
            <TextField {...p} label={t("filters.centro")} size="small" />
          )}
          sx={{ width: 220 }}
        />
        <Button variant="contained" onClick={lanzar}>
          {t("cargaFormato.filtros.generar")}
        </Button>
      </Stack>

      {status && !status.done && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={24} />
          <Typography>{t("cargaFormato.estado.generando")}</Typography>
        </Stack>
      )}

      {status?.done && status.result?.url && (
        <Alert severity="success" sx={{ width: "fit-content" }}>
          XLSX:&nbsp;
          <Button
            variant="text"
            size="small"
            onClick={handleDownload}
            sx={{ textTransform: "none", textDecoration: "underline", p: 0, minWidth: "auto" }}
          >
            {t("cargaFormato.estado.descargar")}
          </Button>
        </Alert>
      )}

      {status?.done && (
        <Paper sx={{ p: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("cargaFormato.tabla.profesor")}</TableCell>
                <TableCell>{t("cargaFormato.tabla.centro")}</TableCell>
                <TableCell>{t("cargaFormato.tabla.formato")}</TableCell>
                <TableCell>{t("cargaFormato.tabla.reservas")}</TableCell>
                <TableCell>{t("cargaFormato.tabla.horas")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r: CargaFormatoRow, idx: number) => (
                <TableRow key={`${r.profesorId}-${r.centroId}-${r.formato}-${idx}`}>
                  <TableCell>{r.profesor}</TableCell>
                  <TableCell>{r.centro}</TableCell>
                  <TableCell>{r.formato}</TableCell>
                  <TableCell>{r.reservas}</TableCell>
                  <TableCell>{r.horas.toFixed(1)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}
